/**
 * The costs module's state: live from state.ext.costs (kept current by SSE), or demo spend in
 * mock mode. Budget and billing changes go to the hub, or to the demo store in mock mode.
 */
import type { BillingMode, Budget, BudgetInput, CostsState, Id, SessionUsage } from '@orgus/protocol';
import { useShallow } from 'zustand/react/shallow';
import { api } from '../../lib/api.ts';
import { useOrgus, useExt } from '../../lib/store.ts';
import type { Entities } from '../../lib/store.ts';
import { useNow } from '@orgus/ui';
import { buildMockCosts, useMockCosts } from './mock.ts';
import { sessionCostIndex } from './model.ts';

// Demo spend is derived once per change, however many components ask for it.
let mockCache: { key: unknown[]; value: CostsState } | null = null;
function mockCosts(e: Pick<Entities, 'sessions' | 'projects' | 'machines'>, minute: number, budgets: Budget[], billing: BillingMode, names: Record<string, string>): CostsState {
  const key = [e.sessions, e.projects, e.machines, minute, budgets, billing, names];
  if (mockCache && mockCache.key.every((k, i) => k === key[i])) return mockCache.value;
  const value = buildMockCosts(e, minute, budgets, billing, names);
  mockCache = { key, value };
  return value;
}

export function useCosts(): CostsState | undefined {
  const mode = useOrgus((s) => s.mode);
  const live = useExt<CostsState>('costs');
  const sessions = useOrgus((s) => s.sessions);
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const { budgets, billing, names } = useMockCosts(useShallow((s) => ({ budgets: s.budgets, billing: s.billing, names: s.names })));
  const now = useNow(60_000);
  const minute = Math.floor(now / 60_000) * 60_000;
  if (mode !== 'mock') return live;
  return mockCosts({ sessions, projects, machines }, minute, budgets, billing, names);
}

const indexes = new WeakMap<CostsState, Record<Id, SessionUsage>>();

/** One session's usage (cost per model), or undefined when the hub has none for it. */
export function useSessionCost(id: Id): SessionUsage | undefined {
  const c = useCosts();
  if (!c) return undefined;
  let idx = indexes.get(c);
  if (!idx) indexes.set(c, (idx = sessionCostIndex(c)));
  return idx[id];
}

function apply(c: CostsState | undefined): void {
  if (c) useOrgus.getState().applyExt({ ext: 'costs', type: 'state', data: c });
}

const isMock = () => useOrgus.getState().mode === 'mock';

export async function saveBudget(input: BudgetInput, id?: Id): Promise<void> {
  if (isMock()) {
    useMockCosts.getState().upsert(input, id);
    return;
  }
  const res = id
    ? await api<{ state: CostsState }>(`/api/costs/budgets/${encodeURIComponent(id)}`, { method: 'PUT', body: input })
    : await api<{ state: CostsState }>('/api/costs/budgets', { method: 'POST', body: input });
  apply(res.state);
}

export async function deleteBudget(id: Id): Promise<void> {
  if (isMock()) {
    useMockCosts.getState().remove(id);
    return;
  }
  const res = await api<{ state: CostsState }>(`/api/costs/budgets/${encodeURIComponent(id)}`, { method: 'DELETE' });
  apply(res.state);
}

export async function setBilling(billing: BillingMode): Promise<void> {
  if (isMock()) {
    useMockCosts.getState().setBilling(billing);
    return;
  }
  apply(await api<CostsState>('/api/costs/settings', { method: 'PUT', body: { billing } }));
}

/** Rename an account (ADR 0067); an empty name goes back to the automatic one. */
export async function renameAccount(key: string, name: string): Promise<void> {
  if (isMock()) {
    useMockCosts.getState().rename(key, name);
    return;
  }
  apply(await api<CostsState>(`/api/costs/accounts/${encodeURIComponent(key)}`, { method: 'PUT', body: { name } }));
}

/** Where the CSV export downloads from (same origin, the session cookie signs it). */
export function exportUrl(month?: string): string {
  return `/api/costs/export.csv${month ? `?month=${encodeURIComponent(month)}` : ''}`;
}
